<template>
  <div :class="classes">
    <Disclosure v-slot="{ open }" :default-open="defaultOpen">
      <DisclosureButton class="ui-card-accordion__button">
        <span class="ui-card-accordion__title text-button">{{ title }}</span>

        <IconChevronDown
          v-if="$slots['hidden']"
          class="ui-card-accordion__arrow"
          :class="{ open }"
        />
      </DisclosureButton>

      <DisclosurePanel static>
        <div class="ui-card-accordion__panel">
          <slot></slot>

          <UiTransitionExpand v-if="$slots['hidden']">
            <div v-show="open" class="ui-card-accordion__panel-hidden">
              <slot name="hidden"></slot>
            </div>
          </UiTransitionExpand>
        </div>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>

<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import UiTransitionExpand from './UiTransitionExpand.vue'
import IconChevronDown from './icons/IconChevronDown.vue'
import { computed } from 'vue'

type Props = {
  title: string
  defaultOpen?: boolean
  variant?: 'default' | 'outlined'
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
})

const classes = computed(() => ({
  'ui-card-accordion': true,
  [`ui-card-accordion--${props.variant}`]: !!props.variant,
  [`${props.class}`]: !!props.class,
}))
</script>

<style lang="scss">
@import '../styles/config';

.ui-card-accordion__item {
  border-bottom: 1px solid $black;

  &:first-child {
    border-top: 1px solid $black;
  }
}

.ui-card-accordion__button {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 2.5rem;
  padding: 0 0.75rem;
  color: $white;
  background-color: $black;
  border-radius: 0.25rem 0.25rem 0 0;
}

.ui-card-accordion__title {
  display: block;
  margin-right: auto;
  color: inherit;
}

.ui-card-accordion__arrow {
  width: 1.25rem;
  height: 1.25rem;
  @include transition('transform');

  &.open {
    transform: scaleY(-1);
  }
}

.ui-card-accordion__panel {
  padding: 0.75rem;
  background-color: $white;
  border: 1px solid $gray-200;
  border-top: 0;
  border-radius: 0 0 0.25rem 0.25rem;

  .no-border & {
    border: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

.ui-card-accordion--outlined {
  .ui-card-accordion__button {
    color: $black;
    background: none;
    border: 1px solid $gray-200;
  }
}
</style>
